import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, Option, Spinner, Tooltip } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useAppContext } from "../../AppContext";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { getOrganizations } from "../../services/organizationService";
import { Module } from "../../models/module";
import { Organization } from "../../models/organizations";
import { CreateUserGroup, UserGroupEmail, UserGroupSaveResponse } from "../../models/userGroup";
import { createUserGroup } from "../../services/userGroupService";
import { getInvitedEmailsMessage } from "../../utils/userGroupInvite";
import NotificationDialog from "./NotificationDialog";

type Props = {
  onClose: () => void;
  open: boolean;
  onGroupCreated?: () => void;
};

type FormValues = {
  organization: Organization | null;
  module: Module | null;
  groupName: string;
  userEmails: string[];
  adminEmails: string[];
};

const UserGroupsDialog = (props: Props) => {
  const {user} = useAppContext();

  const [modules, setModules] = useState<Module[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [userEmailInputValue, setUserEmailInputValue] = useState("");
  const [userEmailError, setUserEmailError] = useState("");
  const [adminEmailInputValue, setAdminEmailInputValue] = useState("");
  const [adminEmailError, setAdminEmailError] = useState("");
  const [invitedEmailsMessage, setInvitedEmailsMessage] = useState<string | null>(null);

  const { control, handleSubmit, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      organization: null,
      module: null,
      groupName: '',
      userEmails: [],
      adminEmails: [],
    }
  });

  const userEmails = watch('userEmails');
  const adminEmails = watch('adminEmails');
  const noAdminSelected = adminEmails.length === 0;
  const isGeneral = user?.roles?.some((r: any) => r.roleType === 'General') ?? false;

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmailChip = (
    value: string,
    fieldName: 'userEmails' | 'adminEmails',
    currentList: string[],
    otherList: string[],
    setInputValue: (value: string) => void,
    setError: (value: string) => void
  ) => {
    if (!value) {
      setInputValue("");
      return;
    }

    if (!isValidEmail(value)) {
      setError("Värdet är inte en giltig e-postadress");
      return;
    }

    if (currentList.includes(value) || otherList.includes(value)) {
      setError("E-postadressen finns redan tillagd");
      return;
    }

    setValue(fieldName, [...currentList, value]);
    setInputValue("");
    setError("");
  };

  const handleRemoveEmailChip = (
    chipToRemove: string,
    fieldName: 'userEmails' | 'adminEmails',
    currentList: string[]
  ) => {
    setValue(fieldName, currentList.filter(chip => chip !== chipToRemove));
  };

  const onSubmit = async (data: FormValues) => {
    console.log('Form submitted:', data);
    
    // Map emails to UserGroupEmail[] with IsAdmin set per field
    const emails: UserGroupEmail[] = [
      ...data.adminEmails.map(email => ({ Email: email, IsAdmin: true })),
      ...data.userEmails.map(email => ({ Email: email, IsAdmin: false })),
    ];

    const newGroup: CreateUserGroup = {
      UserGroupName: data.groupName,
      OrganizationID: data.organization?.organizationID!,
      ModuleID: data.module?.moduleID!,
      Emails: emails
    };

    setCreatingGroup(true);
    try {
      const result: UserGroupSaveResponse = await createUserGroup(newGroup);
      props.onGroupCreated?.();
      props.onClose();
      if (result?.invitedEmails && result.invitedEmails.length > 0) {
        setInvitedEmailsMessage(getInvitedEmailsMessage(result.invitedEmails));
      }
    } catch (err) {
      console.error('Error creating user group:', err);
    } finally {
      setCreatingGroup(false);
    }
  };

  useEffect(() => {

    const fetchOrganizations = async () => {
      if(!user){
        setOrganizations([]);
        setModules([]);
        return;
      }
      setLoading(true);
      try{
        if(user.roles.some((r: any) => r.roleType === 'General')) {
          const orgRes = await getOrganizations();
          const filteredOrgs = orgRes.filter((o: Organization) => o.organizationTypeID == 2);
          setOrganizations(filteredOrgs);
          setModules(user.modules.filter((m: Module) => m.moduleType === 'Tendering'));
          if (filteredOrgs.length === 1) {
            setValue('organization', filteredOrgs[0]);
          }
        }
        else{
          setOrganizations([user.organization]);
          const adminModules: Module[] = user.assignments
            .filter((a: any) => a.role.roleName == "Ramavtalsansvarig" || a.role.roleName == "Kontoadmin")
            .map((a: any) => a.module);
          setModules(adminModules.filter((m, i) => adminModules.findIndex(x => x.moduleID === m.moduleID) === i));
          setValue('organization', user.organization);
        }
      }
      catch(err){
        console.log(err);
      }
      finally {
        setLoading(false);
      }
    }
    if(props.open){
      reset(); // Reset form when dialog opens
      fetchOrganizations();
    }

  }, [props.open, reset]);

  return(
    <>
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={props.onClose}
              />
            }
          >
            Skapa grupp
          </DialogTitle>
          <DialogContent>
            {!user ? (
              <div className="flex justify-center items-center py-8">
                Du måste vara inloggad för att skapa en grupp.
              </div>
            ) : loading ? (
              <div className="flex justify-center items-center py-8">
                <Spinner label="Laddar..." />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Controller
                  name="organization"
                  control={control}
                  rules={{ required: "Organisation måste väljas" }}
                  render={({ field, fieldState }) => (
                    <Field label="Organisation:" required validationMessage={fieldState.error?.message} validationState={fieldState.error ? "error" : "none"}>
                      <Dropdown
                        placeholder="Välj organisation"
                        value={field.value?.organizationName ?? ''}
                        selectedOptions={field.value ? [String(field.value.organizationID)] : []}
                        disabled={organizations.length === 1}
                        onOptionSelect={(_, data) => {
                          const org = organizations.find((o: any) => String(o.organizationID) === data.optionValue);
                          if (org) {
                            field.onChange(org);
                          }
                        }}
                        positioning={{autoSize: false}}
                        listbox={{
                          style: {
                            maxHeight: '300px',
                            overflowY: 'auto'
                          },
                        }}
                      >
                        {organizations.map((org: any) => (
                          <Option key={org.organizationID} value={String(org.organizationID)} text={org.organizationName}>
                            {org.organizationName}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  )}
                />

                <Controller
                  name="module"
                  control={control}
                  rules={{ required: "Ramavtal måste väljas" }}
                  render={({ field, fieldState }) => (
                    <Field label="Ramavtal:" required validationMessage={fieldState.error?.message} validationState={fieldState.error ? "error" : "none"}>
                      <Dropdown
                        placeholder="Välj ramavtal"
                        value={field.value?.moduleName ?? ''}
                        selectedOptions={field.value ? [String(field.value.moduleID)] : []}
                        onOptionSelect={(_, data) => {
                          const mod = modules.find((m: any) => String(m.moduleID) === data.optionValue);
                          if (mod) {
                            field.onChange(mod);
                          }
                        }}
                        positioning={{autoSize: false}}
                        listbox={{
                          style: {
                            maxHeight: '300px',
                            overflowY: 'auto'
                          },
                        }}
                      >
                        {modules.map((mod: any) => (
                          <Option key={mod.moduleID} value={String(mod.moduleID)} text={mod.moduleName}>
                            {mod.moduleName}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  )}
                />

                <Controller
                  name="groupName"
                  control={control}
                  rules={{ required: "Gruppnamn måste anges" }}
                  render={({ field, fieldState }) => (
                    <Field label="Gruppnamn:" required validationMessage={fieldState.error?.message} validationState={fieldState.error ? "error" : "none"}>
                      <Input
                        value={field.value}
                        onChange={(e, data) => field.onChange(data.value)}
                      />
                    </Field>
                  )}
                />

                <Field label="Lägg till behörighetsansvarig (E-post):" required validationMessage={adminEmailError} validationState={adminEmailError ? "error" : "none"}>
                  <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-1">
                    {adminEmails.map((chip, index) => (
                      <div key={index} className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-[#0078d4] text-white">
                        <span>{chip}</span>
                        <Button
                          appearance="transparent"
                          size="small"
                          icon={<DismissRegular />}
                          style={{ color: "white" }}
                          onClick={() => handleRemoveEmailChip(chip, 'adminEmails', adminEmails)}
                        />
                      </div>
                    ))}
                    <Input
                      type="email"
                      value={adminEmailInputValue}
                      onChange={(e, data) => {
                        setAdminEmailInputValue(data.value);
                        setAdminEmailError("");
                        if (data.value.endsWith(".se")) {
                          handleAddEmailChip(data.value, 'adminEmails', adminEmails, userEmails, setAdminEmailInputValue, setAdminEmailError);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddEmailChip(adminEmailInputValue, 'adminEmails', adminEmails, userEmails, setAdminEmailInputValue, setAdminEmailError);
                        }
                      }}
                      placeholder="Lägg till e-post..."
                      className="flex-grow min-w-[200px]"
                      appearance="underline"
                    />
                  </div>
                </Field>
                <span className="text-xs text-gray-500 -mt-2">
                  Minst en behörighetsansvarig måste läggas till för att gruppen ska kunna skapas.
                </span>

                {isGeneral && (
                  <Field label="Lägg till kommunanvändare (E-post):" validationMessage={userEmailError} validationState={userEmailError ? "error" : "none"}>
                    <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-1">
                      {userEmails.map((chip, index) => (
                        <div key={index} className="flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                          <span>{chip}</span>
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={<DismissRegular />}
                            onClick={() => handleRemoveEmailChip(chip, 'userEmails', userEmails)}
                          />
                        </div>
                      ))}
                      <Input
                        type="email"
                        value={userEmailInputValue}
                        onChange={(e, data) => {
                          setUserEmailInputValue(data.value);
                          setUserEmailError("");
                          if (data.value.endsWith(".se")) {
                            handleAddEmailChip(data.value, 'userEmails', userEmails, adminEmails, setUserEmailInputValue, setUserEmailError);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEmailChip(userEmailInputValue, 'userEmails', userEmails, adminEmails, setUserEmailInputValue, setUserEmailError);
                          }
                        }}
                        placeholder="Lägg till e-post..."
                        className="flex-grow min-w-[200px]"
                        appearance="underline"
                      />
                    </div>
                  </Field>
                )}
              </div>
            )}

          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose} disabled={creatingGroup}>Avbryt</Button>
            {noAdminSelected || !user ? (
              <Tooltip
                content={!user ? "Du måste vara inloggad för att skapa en grupp." : "Minst en behörighetsansvarig måste läggas till innan gruppen kan skapas."}
                relationship="label"
              >
                <Button
                  appearance="primary"
                  disabledFocusable
                  icon={creatingGroup ? <Spinner size="tiny" /> : undefined}
                >
                  Spara
                </Button>
              </Tooltip>
            ) : (
              <Button
                appearance="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={creatingGroup || loading}
                icon={creatingGroup ? <Spinner size="tiny" /> : undefined}
              >
                {creatingGroup ? 'Skapar grupp...' : 'Spara'}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <NotificationDialog
      isOpen={!!invitedEmailsMessage}
      message={invitedEmailsMessage ?? ""}
      onClose={() => setInvitedEmailsMessage(null)}
    />
    </>
  )
}

export default UserGroupsDialog;